import React, {ReactNode} from 'react'
import {Nullable} from '../../../../app/TypeHelpers'
import styled from 'styled-components'
import {StyledInput} from '../../../../styledComponents/StyledTypeDefinitions'
import {createUUID} from '../../../../stringHelper/stringHelpers'
import {ErrorHandlingFormProps} from '../hoc/createErrorHandlingForm'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {StyledSecondaryButton} from '../../../../styledComponents/Button'
import {FontSize} from '../../../../styledComponents/Constants'
import {withErrorSubscribe} from '../hoc/withErrorSubscribe'
import PPImagePreviewForResident from './PPImagePreviewForResident'
import {
    ListGridContainer
} from '../../../../__NoAutomatedTests/components/applications/common/resident/NatResidentApplicationsCommon'

export interface ImageInformation {
    fileName: Nullable<string>
    fileData: Nullable<string | ArrayBuffer | null>
    fileNumber: Nullable<number>
    path?: Nullable<string>
    fileIndex?: number
    deleteFlag?: boolean
}

interface Props {
    images: Nullable<ImageInformation[]>
    maxInput: number
    eventKey: string
    inputProps?: React.InputHTMLAttributes<HTMLInputElement>
    onChange?: (image: ImageInformation) => void
}

interface State {
    clearError: boolean
}

class PPImageInputWithPreviewForResident extends React.Component<Props & ErrorHandlingFormProps, State> {

    constructor(props: Readonly<Props & ErrorHandlingFormProps>) {
        super(props)

        this.state = {
            clearError: false,
        }

        this.handleChange = this.handleChange.bind(this)
        this.handleDelete = this.handleDelete.bind(this)
    }

    async getBinaryString(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            let binary: string = ''
            const reader = new FileReader()
            reader.onload = () => {
                if (!reader.result) {
                    reject('reader.result is null')
                }
                const bytes = new Uint8Array(reader.result as ArrayBuffer)
                const length = bytes.byteLength
                for (let i: number = 0; i < length; i++) {
                    const tmpByte = bytes[i]
                    binary += String.fromCharCode(tmpByte)
                }
                resolve(binary)
            }
            reader.readAsArrayBuffer(file)
        })
    }

    async handleChange(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
        this.setState({clearError: false})
        if (!this.props.onChange) {
            return
        }

        if (event.target.files === null ||
            event.target.files.length === 0 ||
            event.target.files[0] === null) {
            return
        }

        const selectedImage: ImageInformation = {
            fileName: event.target.files[0].name,
            fileData: btoa(await this.getBinaryString(event.target.files[0])),
            fileNumber: null,
            path: null,
            fileIndex: this.props.images?.length ?? 0,
            deleteFlag: false
        }
        this.props.onChange(selectedImage)
    }

    handleDelete(fileIndex: number): void {
        this.setState({clearError: false})
        if (!this.props.onChange) {
            return
        }

        this.props.onChange(
            {
                fileName: null,
                fileData: null,
                fileNumber: null,
                path: null,
                fileIndex: fileIndex,
                deleteFlag: true,
            }
        )
    }

    render(): ReactNode {
        const id: string = createUUID()
        const {images, maxInput, eventKey, inputProps} = this.props

        let notDeleteFileCount = 0
        images?.forEach((imagesInfo) => {
            if (!imagesInfo.deleteFlag || imagesInfo.path) {
                notDeleteFileCount++
            }
        })

        return <>
            <ListGridContainer fontSize={FontSize.Small}>
                <div>・</div>
                <div>1ファイルのみ添付可能です。</div>
                <div>・</div>
                <div>ファイルは画像でご提出ください。</div>
            </ListGridContainer>
            {
                (notDeleteFileCount === 0) ?
                    <PPImagePreviewForResident
                        eventKeys={[eventKey]}
                        eventEmitter={this.props.eventEmitter}
                    /> :
                    images?.map((image, index) => (
                        !image.deleteFlag &&
                        <PPImagePreviewForResident
                            key={index}
                            fileName={image.fileName}
                            fileData={image.fileData}
                            filePath={image.path}
                            fileIndex={index}
                            onDelete={this.handleDelete}
                            eventKeys={maxInput > 1
                                ? [eventKey + '[' + index + ']']
                                : [eventKey]}
                            eventEmitter={this.props.eventEmitter}
                        />
                    ))
            }
            {
                (!maxInput || notDeleteFileCount < maxInput) &&
                <>
                    <BlockSpacer height={'30px'}/>
                    <UploadButton>
                        <UploadButtonLabel htmlFor={id}>
                            {notDeleteFileCount ? '画像を追加する' : '画像を選択する'}
                        </UploadButtonLabel>
                    </UploadButton>
                    <InvisibleInput
                        {...inputProps}
                        id={id}
                        type="file"
                        value={''}
                        multiple={false}
                        onChange={this.handleChange}
                    />
                </>
            }
        </>
    }
}

const InvisibleInput: StyledInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
  appearance: none;
  position: absolute;
`

const UploadButtonLabel = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  cursor: pointer;
  position:absolute;
  top: 0;
  left: 0;
`

const UploadButton = styled(StyledSecondaryButton)`
  position: relative;
`

export default withErrorSubscribe(PPImageInputWithPreviewForResident)
